import Instana from '@instana/react-native-agent';
import {Platform as RNPlatform} from 'react-native';
import Settings from '../Settings';
import DeviceInfo from '../nativeModules/DeviceInfo';
import PersistentStore from '../services/PersistentStoreService';

/* ---------------------------------------------------------
 * 1.  Compile-time information
 * ------------------------------------------------------- */
const buildTypeMeta = Settings.buildType; // default | canteen | …

/* ---------------------------------------------------------
 * 2.  Run-time information
 * ------------------------------------------------------- */

let appVersionMeta = '';
let isInitialized = false;

function getEnvKey() {
  return (Settings.env ?? 'PROD').toUpperCase();
}

function getInstanaConfig() {
  const key = getEnvKey();
  const config = Settings.instana[key] ?? Settings.instana.PROD;
  return config;
}

/* ---------------------------------------------------------
 * 3.  Public bootstrap
 * ------------------------------------------------------- */
export async function initializeInstana(): Promise<void> {
  try {
    if (isInitialized) {
      return;
    }
    const appVersion = await DeviceInfo.getVersion();
    const buildNumber = await DeviceInfo.getBuildNumber();
    appVersionMeta = `${appVersion} (${buildNumber})`;
    const instanaCfg = getInstanaConfig();
    Instana.setup(instanaCfg.key, instanaCfg.url, {
      enableW3CHeaders: true,
      enableAutoScreenTracking: true,
      enableAutoNetworkTracking: true,
      enableCrashReporting: true,
      enablePerformanceMonitoring: true,
      rateLimits: 1,
    });
    const metaData = [
      ['platform', RNPlatform.OS],
      ['environment', getEnvKey().toLowerCase()],
      ['appVersion', appVersionMeta],
      ['template', buildTypeMeta],
      ['appDisplayName', Settings.appDisplayName],
      ['buildType', Settings.buildType],
      ['is365Pay', Settings.is365Pay()],
      ['isCanteen', Settings.isCanteen()],
      ['isRefive', Settings.isRefive()],
      ['isCompanyKitchen', Settings.buildType === 'companykitchen'],
    ];
    metaData.forEach(([k, v]) => Instana.setMeta(k, String(v)));
    await identifyUserFromPersistentStore();
    Instana.setView('Bootstrap');
    isInitialized = true;
  } catch (error) {
    throw error;
  }
}

/* ---------------------------------------------------------
 * 4.  Helpers
 * ------------------------------------------------------- */
export async function identifyUserFromPersistentStore(): Promise<void> {
  try {
    const acc = await PersistentStore.getAccountDetails?.();
    if (!acc) {
      return;
    }
    acc.accountId && Instana.setUserID(String(acc.accountId));
    acc.username && Instana.setUserEmail(acc.username);
    let fullName = acc.username;
    if (typeof acc === 'object') {
      const firstName =
        typeof (acc as any).firstName === 'string'
          ? (acc as any).firstName
          : '';
      const lastName =
        typeof (acc as any).lastName === 'string' ? (acc as any).lastName : '';
      if (firstName || lastName) {
        fullName = `${firstName} ${lastName}`.trim();
      }
    }
    if (fullName) {
      Instana.setUserName(fullName);
    }
  } catch (error) {}
}

function withCommonMeta(extra?: Record<string, any>) {
  return {
    ...extra,
    appVersion: appVersionMeta,
    buildType: buildTypeMeta,
    environment: getEnvKey().toLowerCase(),
    timestamp: new Date().toISOString(),
  };
}

/* ---------------------------------------------------------
 * 5.  Public tracking helpers
 * ------------------------------------------------------- */
export function trackScreenView(
  screenName: string,
  additional?: Record<string, any>,
): void {
  Instana.setView(screenName);
  Instana.reportEvent('screen_view', {
    viewName: screenName,
    meta: withCommonMeta({screen: screenName, ...additional}),
  });
}

export function trackUserAction(
  action: string,
  additional?: Record<string, any>,
): void {
  Instana.reportEvent('user_action', {
    meta: withCommonMeta({action, ...additional}),
  });
}
